/* eslint-disable max-len */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import moment from 'moment';

import saveUseProduct from 'actions/product/saveUseProduct.action';
import basketIcon from 'assets/images/basket/Stock-order-icon.svg';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import CurrencySign from 'components/price/CurrencySign.component';
import ModalDeleteProduct from 'components/stock/ModalDeleteProduct.component';
import PUSHER from 'config/pusher.singleton';
import DELAY from 'constants/delay.const';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { usePathname } from 'hooks/usePathname.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonCurrency } from 'selectors/currentSalon.selectors';
import getAnyOrNull from 'utils/accessors/getAnyOrNull.util';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import delayed from 'utils/functions/delayed.util';
import currencySign from 'utils/intl/currencySign.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';
import isEmptyObject from 'utils/predicates/object/isEmptyObject.util';

import ConfirmationDialog from 'clientProfile/components/common/ConfirmationDialog';
import PopupModalContainer from 'common/PopupModal';
import { setDatePickerDate } from 'reports/actions';
import DateRangePicker from 'reports/Common/DateRangePicker';
import {
  addItemsToBasket,
  applyCostPriceChange,
  applyFilters,
  applyRetailPriceChange,
  bulkUpdate,
  clearFilter,
  deleteOrderItem,
  deleteProduct,
  editOrderItem,
  editStock,
  editStockMaxHolding,
  editStockMinHolding,
  emptyOrderBasket,
  generateOrderData,
  getLastOrderData,
  getSavedBasket,
  getStockCount,
  getStockData,
  loadBrands,
  loadCategories,
  loadSubBrands,
  PUSH_MSG_UPDATE_STOCK,
  revertCostorRetailPrice,
  saveBasket,
  sortData,
  tickSelectAll,
  toggleAllOrderItem,
  toggleFilter,
  toggleFilterValue,
  toggleOrderItem,
  toggleSelectItem,
  toogleCheckAll,
} from 'stocks/actions';
import EditManufacturerProductSidebar from 'stocks/components/common/EditManufacturerProductSidebar';
import FilterControl from 'stocks/components/common/Filter';
import InfoItem from 'stocks/components/common/InfoItem';
import ManageItemsSidebarContainer from 'stocks/components/common/ManageItemsSidebar';
import TableLastStockMovement from 'stocks/components/common/TableLastStockMovement';
import TableStocksManager from 'stocks/components/common/TableStocksManager';
import TableStocksOrdering from 'stocks/components/common/TableStocksOrdering';
import TableStocksUsedSold from 'stocks/components/common/TableStocksUsedSold';
import { stockManagerPdfGenerator } from 'stocks/components/common/utils/stockManagerPdfGenerator';
import HeaderStocksFilter from 'stocks/components/HeaderStocksFilter';
import HeaderStocksInfoContainer from 'stocks/components/HeaderStocksInfoContainer';
import HeaderStocksNavigation from 'stocks/components/HeaderStocksNavigation';
import ModalConfirmation from 'stocks/components/Manager/ModalConfirmation.component';
import UseProductModal from 'stocks/components/Manager/UseProductModal.component';
import UseProductSearch from 'stocks/components/Manager/UseProductSearch.component';
import { ColumnsOrder, STOCK_HOLDING_PDF_TABLE_ID, STOCK_LAST_MOVEMENT_PDF_TABLE_ID, STOCK_MANAGER_PDF_TABLE_ID, STOCK_SOLD_PDF_TABLE_ID } from 'stocks/consts';
import { calculateCurrentLayout, mapStockOrders, mapStocks } from 'stocks/utils';

import CN from './ManagerLayout.module.scss';

const L = logger('ManagerLayout');

const ManagerLayout = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const pathname = usePathname();
  const routeMatch = useRouteMatch();
  const location = { pathname };

  // Redux state
  const isFetching = useSelector((state) => state.stockManagement.stock.isFetching);
  const salonId = useSelector((state) => state.currentSalon.id);
  const lastDatesSelected = useSelector((state) => state.reports.lastDatesSelected);
  const reduxErrorMessage = useSelector((state) => state.stockManagement.stock.errorMessage);
  const data = useSelector((state) => state.stockManagement.stock.filtered);
  const sorted = useSelector((state) => state.stockManagement.stock.sorted);
  const filters = useSelector((state) => state.stockManagement.stock.filters);
  const orderingData = useSelector((state) => state.stockManagement.stock.orderingData);
  const isSavingStock = useSelector((state) => state.stockManagement.stock.isPosting);
  const hasAnyProducts = useSelector((state) => !!state.stockManagement.stock.apiData.length);
  const allItemsSelected = useSelector((state) => state.stockManagement.stock.allItemsSelected);
  const brandList = useSelector((state) => state.stockManagement.stock.brandList);
  const subBrandList = useSelector((state) => state.stockManagement.stock.subBrandList);
  const categoryList = useSelector((state) => state.stockManagement.stock.categoryList);
  const orderItems = useSelector((state) => state.stockManagement.stock.orderItems);
  const basketSaved = useSelector((state) => state.stockManagement.stock.basketSaved);
  const lastOrderDate = useSelector((state) => state.stockManagement.stock.lastOrderDate);
  const geratedOrderNumber = useSelector((state) => state.stockManagement.stock.geratedOrderNumber);
  const allOrderItemsSelected = useSelector((state) => state.stockManagement.stock.allOrderItemsSelected);
  const currentCurrencySign = useSelector(selectCurrentSalonCurrency);

  // Local state
  const [columnOrder, setColumnOrder] = useState(() => calculateCurrentLayout(pathname));
  const [productToDelete, setProductToDelete] = useState(-1);
  const [filterByBrand, setFilterByBrand] = useState(false);
  const [filterBySubBrand, setFilterBySubBrand] = useState(false);
  const [filterByCategory, setFilterByCategory] = useState(false);
  const [search, setSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [manageItems, setManageItems] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [deleteModalSuccess, setDeleteModalSuccess] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [editedStock, setEditedStock] = useState(null);
  const [clearBtn, setClearBtn] = useState(false);
  const [lowInStock, setLowInStock] = useState(false);
  const [modalUseProduct, setModalUseProduct] = useState(false);
  const [valueProduct, setValueProduct] = useState('');
  const [valueQuantity, setValueQuantity] = useState(1);
  const [productId, setProductId] = useState(0);
  const [productDropdown, setProductDropdown] = useState(false);
  const [error, setError] = useState(false);
  const [modalSuccessful, setModalSuccessful] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState('');

  const inputName = 'inputName';
  const inputQuantity = 'inputQuantity';

  // Refs for accessing latest values in effects
  const salonIdRef = useRef(salonId);
  const orderItemsRef = useRef(orderItems);
  const lastDatesSelectedRef = useRef(lastDatesSelected);
  salonIdRef.current = salonId;
  orderItemsRef.current = orderItems;
  lastDatesSelectedRef.current = lastDatesSelected;

  // Mount effect (replaces UNSAFE_componentWillMount)
  useEffect(() => {
    dispatch(getStockData(salonIdRef.current));

    PUSHER.redux.subscribe({
      channelName: `${salonIdRef.current}_channel`,
      eventName: 'productQuantity',
      actionType: PUSH_MSG_UPDATE_STOCK,
    });

    dispatch(loadBrands(salonIdRef.current));
    dispatch(loadSubBrands(salonIdRef.current));
    dispatch(loadCategories(salonIdRef.current));
    dispatch(getLastOrderData(salonIdRef.current, false));

    return () => {
      dispatch(saveBasket(salonIdRef.current, orderItemsRef.current));
      PUSHER.redux.unsubscribe({
        channelName: `${salonIdRef.current}_channel`,
        eventName: 'productQuantity',
        actionType: PUSH_MSG_UPDATE_STOCK,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Sync columnOrder and errorMessage when pathname/reduxErrorMessage change
  useEffect(() => {
    setColumnOrder(calculateCurrentLayout(pathname));
  }, [pathname]);

  useEffect(() => {
    setErrorMessage(reduxErrorMessage);
  }, [reduxErrorMessage]);

  // Replaces UNSAFE_componentWillReceiveProps date range check
  const prevDatesRef = useRef(lastDatesSelected);
  useEffect(() => {
    const prev = prevDatesRef.current;
    prevDatesRef.current = lastDatesSelected;

    const noUsedSoldInRangeData = data.length > 0 ? data.some((stock) => !fromAnyToBool(stock.usedSoldInRangeData) || isEmptyObject(stock.usedSoldInRangeData)) : false;

    if (lastDatesSelected.fromDate.toISOString() !== prev.fromDate.toISOString() || lastDatesSelected.toDate.toISOString() !== prev.toDate.toISOString() || noUsedSoldInRangeData) {
      dispatch(getStockCount(salonId, lastDatesSelected.fromDate, lastDatesSelected.toDate));
      dispatch(getSavedBasket(salonId));
    }
  }, [dispatch, salonId, lastDatesSelected, data]);

  // Search func
  const searchFunc = useCallback(
    (el) =>
      el
        .getName()
        .trim()
        .normalize('NFD')
        .replace(/^0+/, '')
        .replace(/'/, '')
        .replace(/'/, '')
        .toLocaleString('en-GB')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[\u00E9]/g, '')
        .search(search) !== -1 ||
      el
        .getBrand()
        .trim()
        .normalize('NFD')
        .replace(/^0+'/, '')
        .replace(/'/, '')
        .replace(/'/, '')
        .toLocaleString('en-GB')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u00E9]/g, '')
        .toLowerCase()
        .search(search) !== -1 ||
      el
        .getFamily()
        .trim()
        .normalize('NFD')
        .replace(/^0+/, '')
        .toLocaleString('en-GB')
        .replace(/'/, '')
        .replace(/'/, '')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u00E9]/g, '')
        .toLowerCase()
        .search(search) !== -1 ||
      el
        .getCategory()
        .trim()
        .normalize('NFD')
        .replace(/^0+/, '')
        .toLocaleString('en-GB')
        .replace(/'/, '')
        .replace(/'/, '')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u00E9]/g, '')
        .toLowerCase()
        .search(search) !== -1 ||
      (el.barcode ? el.barcode.toString() : '')
        .trim()
        .normalize('NFD')
        .replace(/^0+/, '')
        .replace(/'/, '')
        .replace(/'/, '')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u00E9]/g, '')
        .toLowerCase()
        .search(search) !== -1,
    [search],
  );

  // Handlers
  const onSearch = useCallback((v) => {
    setSearch(
      v
        .normalize('NFD')
        .replace(/^0+/, '')
        .replace(/'/, '')
        .replace(/'/, '')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[\u00E9]/g, '')
        .toLowerCase(),
    );
    setClearBtn(true);
  }, []);

  const clearSearch = useCallback(() => {
    setClearBtn(false);
    setSearch('');
    dispatch(toogleCheckAll(false));
  }, [dispatch]);

  const onSelectProductName = useCallback((product) => {
    setValueProduct(product.title);
    setProductId(product.id);
    setProductDropdown(false);
  }, []);

  const onBlurProduct = useCallback(() => {
    delayed(DELAY.blur, () => {
      setProductDropdown((prev) => {
        if (prev) {
          setValueProduct('');
          return false;
        }
        return prev;
      });
    });
  }, []);

  const onSelectProductQuantity = useCallback((product) => {
    setValueQuantity(product.units_in_stock);
  }, []);

  const onSearchProduct = useCallback((evt) => {
    const name = fromAnyToString(getAnyOrVoid('target.name', evt));
    const value = getAnyOrVoid('target.value', evt);

    if (name === 'inputName') {
      setValueProduct(fromAnyToString(value));
      setProductDropdown(true);
    } else if (name === 'inputQuantity') {
      const min = 0;
      if (value < min) {
        setError(true);
      } else {
        setValueQuantity(fromAnyToNumber(value));
        setProductDropdown(false);
        setError(false);
      }
    }
  }, []);

  const onClickSearchProduct = useCallback(() => void setValueProduct(''), []);
  const onClickSearchQuantity = useCallback(() => void setValueQuantity(''), []);

  const handleSaveUseProduct = useCallback(async () => {
    try {
      setModalUseProduct(false);
      setModalErrorMessage('');

      const result = await dispatch(saveUseProduct({ salonId, productId, quantity: valueQuantity }));
      L.unignore('saveUseProduct()', 'getStockCount:', dispatch(getStockCount(salonId, lastDatesSelected.fromDate, lastDatesSelected.toDate)));

      const { errorMessage: errMsg } = asObject(result);
      const errorBody = getAnyOrVoid('error.body', result);

      if (errMsg) {
        L.debug('saveUseProduct()', 'FAIL result:', result, errMsg, errorBody);
        setModalErrorMessage(errMsg);
      } else {
        L.debug('saveUseProduct()', 'PASS result:', result);
        setModalSuccessful(true);
        setValueProduct('');
        setValueQuantity(1);
      }
    } catch (e) {
      L.warn('saveUseProduct()', 'FAIL error:', e);
      setModalErrorMessage(fromAnyToString(asObject(e).message) || t('stocks.managerLayout.defaultErrorMsg'));
    } finally {
      delayed(DELAY.autohide2, () => {
        setModalSuccessful(false);
        setModalErrorMessage('');
      });
    }
  }, [dispatch, salonId, productId, valueQuantity, lastDatesSelected, t]);

  const onSaveEdit = useCallback(
    (editData) => {
      dispatch(editStock(salonId, editData, true));
      setEditedStock(null);
    },
    [dispatch, salonId],
  );

  const onSaveBulkEdit = useCallback(
    (brand, subBrand, category) =>
      new Promise((resolve) => {
        try {
          dispatch(
            bulkUpdate(
              salonId,
              brand,
              subBrand,
              category,
              sorted.filter((c) => c.selected),
            ),
          );

          if (brand !== '' && brandList.filter((c) => c === brand).length === 0) {
            dispatch(loadBrands(salonId));
          }
          if (subBrand !== '' && subBrandList.filter((c) => c === subBrand).length === 0) {
            dispatch(loadSubBrands(salonId));
          }
          if (category !== '' && categoryList.filter((c) => c === category).length === 0) {
            dispatch(loadCategories(salonId));
          }

          setManageItems(false);

          resolve();
        } catch (err) {
          L.debug('onSaveBulkEdit()', 'Reject:', err);
        }
      }),
    [dispatch, salonId, sorted, brandList, subBrandList, categoryList],
  );

  const onSelectAllFilters = useCallback(
    (field, setSelect) => {
      filters.forEach((filter) => {
        if (filter.field === field) {
          Object.keys(filter.values).map((key) => {
            dispatch(toggleFilterValue(filter, key, setSelect));
            return true;
          });
        }
        return true;
      });
    },
    [dispatch, filters],
  );

  const onSortFunction = useCallback(
    (sortOption) => {
      dispatch(sortData(sortOption));
    },
    [dispatch],
  );

  const onBulkDelete = useCallback(() => {
    setBulkDeleteModal(true);
  }, []);

  const onAllDelete = useCallback(() => {
    const deleteSelectedItems = sorted.filter((c) => c.selected === true);
    deleteSelectedItems.forEach((item) => {
      dispatch(deleteProduct(item.id));
      return true;
    });

    setManageItems(false);
    setDeleteModalSuccess(true);
    setBulkDeleteModal(false);
  }, [dispatch, sorted]);

  const onChangeStocksDate = useCallback(
    (startDate, endDate) => {
      dispatch(setDatePickerDate(startDate, endDate));
      dispatch(getStockCount(salonId, startDate, endDate));
    },
    [dispatch, salonId],
  );

  const onSelectItem = useCallback(
    (editItem, isFromOrder) => {
      dispatch(toggleSelectItem(editItem, !editItem.selected));
      if (!manageItems && !isFromOrder) {
        setManageItems(true);
        setFilterByBrand(false);
        setFilterBySubBrand(false);
        setFilterByCategory(false);
        setEditedStock(null);
      }
    },
    [dispatch, manageItems],
  );

  const onCheckAllItems = useCallback(() => {
    if (search) {
      const searchResults = sorted.filter(searchFunc);

      const selected = !allItemsSelected;
      searchResults.forEach((item) => {
        dispatch(toggleSelectItem(item, selected));
      });

      dispatch(tickSelectAll(selected, false));
      if (!searchResults.some((c) => c.selected === true)) {
        setManageItems(false);
      }
    } else {
      dispatch(toogleCheckAll(!allItemsSelected));
    }

    if (!allItemsSelected) {
      setManageItems(true);
      setFilterByBrand(false);
      setFilterBySubBrand(false);
      setFilterByCategory(false);
      setEditedStock(null);
    } else {
      setManageItems(false);
    }
  }, [dispatch, search, sorted, searchFunc, allItemsSelected]);

  const onSelectAllOrderItems = useCallback(() => {
    const selected = !allOrderItemsSelected;
    if (search || lowInStock) {
      if (search) {
        const orderSearchResults = orderingData.filter(searchFunc);
        orderSearchResults.forEach((item) => {
          if (item.isInBasket === false) {
            dispatch(toggleOrderItem(item, selected));
          }
        });
      }
      if (lowInStock) {
        const searchResults = orderingData.filter((c) => (c.units_in_stock || c.max_in_stock ? c.max_in_stock - c.units_in_stock > 0 : false));

        searchResults.forEach((item) => {
          if (item.isInBasket === false) {
            dispatch(toggleOrderItem(item, selected));
          }
        });
      }
      dispatch(tickSelectAll(selected, true));
    } else {
      dispatch(toggleAllOrderItem(!allOrderItemsSelected));
    }
  }, [dispatch, search, lowInStock, orderingData, searchFunc, allOrderItemsSelected]);

  const deleteItem = useCallback(() => {
    dispatch(deleteProduct(productToDelete));
    setProductToDelete(-1);
  }, [dispatch, productToDelete]);

  const attemptDelete = useCallback(
    (id) => {
      dispatch(deleteProduct(id));
    },
    [dispatch],
  );

  const openEditProductSidebar = useCallback((stock) => {
    setFilterByBrand(false);
    setEditedStock(stock);
    setFilterBySubBrand(false);
    setFilterByCategory(false);
    setManageItems(false);
  }, []);

  const openManageItemsSidebar = useCallback(() => {
    setManageItems(true);
    setFilterByBrand(false);
    setFilterBySubBrand(false);
    setFilterByCategory(false);
    setEditedStock(null);
  }, []);

  const closeEditSidebar = useCallback(() => {
    setEditedStock(null);
  }, []);

  const closeManageSidebar = useCallback(() => {
    dispatch(toogleCheckAll(false));
    setManageItems(false);
  }, [dispatch]);

  const showFilterByBrand = useCallback(() => {
    setEditedStock(null);
    setFilterByBrand((prev) => !prev);
    setFilterBySubBrand(false);
    setFilterByCategory(false);
    setIsCategoryOpen(false);
  }, []);

  const showFilterByCategory = useCallback(() => {
    setEditedStock(null);
    setFilterByBrand(false);
    setFilterByCategory((prev) => !prev);
    setFilterBySubBrand(false);
    setIsCategoryOpen(false);
  }, []);

  const showFilterBySubBrand = useCallback(() => {
    setEditedStock(null);
    setFilterByBrand(false);
    setFilterBySubBrand((prev) => !prev);
    setFilterByCategory(false);
    setIsCategoryOpen(false);
  }, []);

  const openDropdown = useCallback(() => {
    setEditedStock(null);
    setFilterByBrand(false);
    setFilterBySubBrand(false);
    setFilterByCategory(false);
    setIsCategoryOpen((prev) => !prev);
  }, []);

  const closeFilter = useCallback(() => {
    dispatch(applyFilters());
    setFilterByBrand(false);
    setFilterBySubBrand(false);
    setFilterByCategory(false);
  }, [dispatch]);

  const close = useCallback(() => {
    setDeleteModalSuccess(false);
    setManageItems(false);
  }, []);

  const cancelDeleteModal = useCallback(() => {
    setBulkDeleteModal(false);
  }, []);

  const hideError = useCallback(() => {
    setErrorMessage('');
  }, []);

  const saveBasketOrder = useCallback(() => {
    dispatch(saveBasket(salonId, orderItems));
  }, [dispatch, salonId, orderItems]);

  const onCheckLowInStock = useCallback(() => {
    setLowInStock((prev) => !prev);
  }, []);

  const onClearFilter = useCallback(() => {
    setLowInStock(false);
    dispatch(clearFilter());
  }, [dispatch]);

  const onGenerateOrderData = useCallback(() => {
    dispatch(generateOrderData(salonId, orderItems));
  }, [dispatch, salonId, orderItems]);

  // HEADER calculations
  const gridData = sorted;
  const categoryKeys = sorted.filter(searchFunc);
  const productLines = data ? categoryKeys.length : 0;
  let totalCostValue = 0;
  let totalRetailValue = 0;
  let itemsUsed = 0;
  let costOfUsed = 0;
  let itemsSold = 0;
  let costOfSold = 0;
  let lastProductEdited = null;
  if (categoryKeys.length > 0) {
    categoryKeys.forEach((item) => {
      if (item.cost_price !== null && item.units_in_stock > 0) {
        totalCostValue += Number(item.cost_price) * item.units_in_stock;
      }
      if (item && item.usedSoldInRangeData) {
        const soldItems = fromAnyToBool(item.usedSoldInRangeData) ? parseFloat(getAnyOrNull('usedSoldInRangeData.sold', item)) : 0;
        const usedItems = fromAnyToBool(item.usedSoldInRangeData) ? parseFloat(getAnyOrNull('usedSoldInRangeData.used', item)) : 0;

        costOfUsed += Number(item.cost_price) * usedItems;
        costOfSold += Number(item.cost_price) * soldItems;
        itemsUsed += usedItems;
        itemsSold += soldItems;
      }
      if (item.units_in_stock > 0 && item.price > 0) {
        totalRetailValue += Number(item.price) * item.units_in_stock;
      }
      return true;
    });
    if (columnOrder === ColumnsOrder.LastStockMovement) {
      const lastStockHeader = data.length > 0 ? [...data].sort((a, b) => (a.latest_change && b.latest_change ? moment(b.latest_change.timestamp).diff(moment(a.latest_change.timestamp)) : 0)) : [];
      lastProductEdited = lastStockHeader[0];
    }
  }
  const brandFilterSelected = filters.filter((d) => d.field === 'brand');
  let filterBrandCount = 0;
  let allBrandSelected = true;
  if (brandFilterSelected[0]) {
    const count = brandFilterSelected[0].values;
    Object.keys(count).map((k) => {
      if (count[k] === true) {
        filterBrandCount++;
      } else {
        allBrandSelected = false;
      }
      return true;
    });
  }
  const subbrandFilterSelected = filters.filter((d) => d.field === 'sub_brand');
  let filterSubBrandCount = 0;
  let allSubBrandSelected = true;
  if (subbrandFilterSelected[0]) {
    const countSub = subbrandFilterSelected[0].values;
    Object.keys(countSub).map((k) => {
      if (countSub[k] === true) {
        filterSubBrandCount++;
      } else {
        allSubBrandSelected = false;
      }
      return true;
    });
  }
  const categoryFilterSelected = filters.filter((d) => d.field === 'category_name');
  let filterCategoryCount = 0;
  let allCategorySelected = true;
  if (categoryFilterSelected[0]) {
    const countCat = categoryFilterSelected[0].values;
    Object.keys(countCat).map((k) => {
      if (countCat[k] === true) {
        filterCategoryCount++;
      } else {
        allCategorySelected = false;
      }
      return true;
    });
  }
  const finalOrderingData = mapStocks(orderingData);
  const orderingSelection = orderingData.filter((c) => c.selected === true && c.isInBasket === false).length;
  const groupOrderItems = mapStockOrders(orderItems);
  let totalOrderCost = 0;
  orderItems.forEach((item) => {
    totalOrderCost += item.cost_price ? item.cost_price * item.quantity : 0;
  });
  let countLowInStock = 0;
  const countLow = gridData.filter((c) => (c.units_in_stock || c.max_in_stock ? c.max_in_stock - c.units_in_stock > 0 : false));
  if (countLow.length > 0) {
    countLowInStock = countLow.length;
  }

  const listOfQuantity = data;
  const uniques = Object.values(
    listOfQuantity.reduce((a, c) => {
      // eslint-disable-next-line no-param-reassign
      a[c.units_in_stock] = c;
      return a;
    }, {}),
  );
  const renderProducts = data
    .filter((product) => valueProduct === '' || product.title.toLowerCase().includes(valueProduct.toLowerCase()))
    .map((product) => (
      <li
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        role="button"
        tabIndex={0}
        onClick={() => {
          onSelectProductName(product);
        }}
        key={product.id}
      >
        {product.title}
      </li>
    ));
  const renderProductsByQuantity = uniques
    .filter((product) => valueQuantity === '' || String(product.units_in_stock).includes(valueQuantity))
    .map((product) => (
      <li
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        role="button"
        tabIndex={0}
        onClick={() => {
          onSelectProductQuantity(product);
        }}
        key={product.id}
      >
        {Number(product.units_in_stock).toFixed(2)}
      </li>
    ));

  const generatePdf = () => {
    const currentDate = moment().format('Do MMMM YYYY');
    const stockManagerHeaderContent = [
      t('stocks.stockManagerPdf.stockList', { date: currentDate }),
      t('stocks.stockManagerPdf.totalCostValue', {
        totalCost: currencySign({ value: totalCostValue, currentSalonAbbr: currentCurrencySign }),
      }),
      t('stocks.stockManagerPdf.totalRetailValue', {
        totalRetail: currencySign({ value: totalRetailValue, currentSalonAbbr: currentCurrencySign }),
      }),
    ];

    const headerMap = {
      [LINK.stocksManager]: {
        content: stockManagerHeaderContent,
        tableId: STOCK_MANAGER_PDF_TABLE_ID,
      },
      [LINK.stocksUsedSold]: {
        content: [],
        tableId: STOCK_SOLD_PDF_TABLE_ID,
      },
      [LINK.stocksMovement]: {
        content: [],
        tableId: STOCK_LAST_MOVEMENT_PDF_TABLE_ID,
      },
      [LINK.stocksHolding]: {
        content: [],
        tableId: STOCK_HOLDING_PDF_TABLE_ID,
      },
    };

    const headerInfo = headerMap[routeMatch.path];

    stockManagerPdfGenerator({
      tableId: headerInfo.tableId,
      headerContent: headerInfo.content,
      date: currentDate,
      salonId,
      t,
    });
  };

  return (
    <div>
      {errorMessage && <PopupModalContainer popupTitle={t('common.error')} popupMessage={errorMessage} buttonText={t('common.buttons.okay')} buttonCallback={hideError} />}
      {productToDelete !== -1 && (
        <ConfirmationDialog
          title={t('stocks.managerLayout.deleteItemModal.title')}
          leftButtonText={t('common.buttons.no')}
          onLeft={() => setProductToDelete(-1)}
          rightButtonText={t('common.buttons.yes')}
          onRight={deleteItem}
          onCancel={() => setProductToDelete(-1)}
        >
          <p>{t('stocks.managerLayout.deleteItemModal.description')}</p>
        </ConfirmationDialog>
      )}
      <HeaderStocksNavigation salonId={salonId} />
      {columnOrder === ColumnsOrder.ManagerLayoutColumnsOrder && (
        <HeaderStocksInfoContainer>
          <div className="scroll has-pdf-btn">
            <div className="scroll-content">
              <div className="flex-info-container">
                <InfoItem text={t('stocks.managerLayout.managerHeaderInfoItems.item1')} value={productLines} tooltip tooltipText={t('stocks.managerLayout.managerHeaderInfoItems.item1Tooltip')} />
                <InfoItem
                  text={t('stocks.managerLayout.managerHeaderInfoItems.item2')}
                  value={
                    <>
                      <CurrencySign value={totalCostValue} />
                    </>
                  }
                  tooltip
                  tooltipText={t('stocks.managerLayout.managerHeaderInfoItems.item2Tooltip')}
                />
                <InfoItem
                  text={t('stocks.managerLayout.managerHeaderInfoItems.item3')}
                  value={
                    <>
                      <CurrencySign value={totalRetailValue} />
                    </>
                  }
                  tooltip
                  tooltipText={t('stocks.managerLayout.managerHeaderInfoItems.item3Tooltip')}
                />
                <InfoItem text={t('stocks.managerLayout.managerHeaderInfoItems.item4')} value={countLowInStock} tooltip tooltipText={t('stocks.managerLayout.managerHeaderInfoItems.item4Tooltip')} />
                <InfoItem
                  text={t('stocks.managerLayout.managerHeaderInfoItems.item5')}
                  // TODO: date localisation
                  value={lastOrderDate ? `${moment(lastOrderDate).format('Do MMM YYYY')}` : '-'}
                />
              </div>
            </div>
          </div>
        </HeaderStocksInfoContainer>
      )}
      {columnOrder === ColumnsOrder.UsedSoldOverDateRange && (
        <HeaderStocksInfoContainer>
          <div className="dpicker-container">
            <DateRangePicker tag="ManagerLayout__range-picker" lastDatesSelected={lastDatesSelected} onChangeDate={onChangeStocksDate} location={location} isFetching={false} />
          </div>
          <div className="scroll">
            <div className="scroll-content">
              <div className="flex-info-container align-center no-padding">
                <InfoItem text={t('stocks.managerLayout.usedSoldHeaderInfoItems.item1')} value={itemsUsed.toFixed(2)} />
                <InfoItem
                  text={t('stocks.managerLayout.usedSoldHeaderInfoItems.item2')}
                  value={
                    <>
                      <CurrencySign value={costOfUsed} />
                    </>
                  }
                />
                <InfoItem text={t('stocks.managerLayout.usedSoldHeaderInfoItems.item3')} value={itemsSold} />
                <InfoItem
                  text={t('stocks.managerLayout.usedSoldHeaderInfoItems.item4')}
                  value={
                    <>
                      <CurrencySign value={costOfSold} />
                    </>
                  }
                />
              </div>
            </div>
          </div>
        </HeaderStocksInfoContainer>
      )}
      {columnOrder === ColumnsOrder.LastStockMovement && (
        <HeaderStocksInfoContainer>
          <div className="scroll">
            <div className="scroll-content">
              <div className="flex-info-container align-center">
                <InfoItem text={t('stocks.managerLayout.lastMovementHeaderInfoItems.item1')} value={lastProductEdited ? lastProductEdited.title : '0'} />
                <InfoItem
                  text={t('stocks.managerLayout.lastMovementHeaderInfoItems.item2')}
                  value={
                    lastProductEdited && lastProductEdited.latest_change
                      ? moment(lastProductEdited.latest_change.timestamp).format('DD-MM-YYYY') // TODO: date localisation
                      : ''
                  }
                />
                <InfoItem
                  text={t('stocks.managerLayout.lastMovementHeaderInfoItems.item3')}
                  value={lastProductEdited && lastProductEdited.latest_change ? lastProductEdited.latest_change.amount : ''}
                />
                <InfoItem
                  text={t('stocks.managerLayout.lastMovementHeaderInfoItems.item4')}
                  value={
                    lastProductEdited && lastProductEdited.latest_change && lastProductEdited.latest_change.type
                      ? lastProductEdited.latest_change.type.charAt(0).toUpperCase() + lastProductEdited.latest_change.type.slice(1).replace('_', ' ')
                      : ''
                  }
                />
              </div>
            </div>
          </div>
        </HeaderStocksInfoContainer>
      )}
      {columnOrder === ColumnsOrder.StockHoldingColumnsOrder && (
        <HeaderStocksInfoContainer>
          <div className="scroll">
            <div className="scroll-content">
              <div className="flex-info-container align-center">
                <div className="stock-ordering">
                  <img src={basketIcon} alt="Basket icon" />
                </div>
                <div className="selected-products">
                  <div>
                    {orderingSelection}
                    {S.space}
                    {orderingSelection >= 2 ? t('stocks.managerLayout.orderingHeaderInfoItems.selectedProducts') : t('stocks.managerLayout.orderingHeaderInfoItems.selectedProduct')}
                  </div>
                  {orderingSelection > 0 && (
                    <button id="add-to-basket" className="btn btn-violet" onClick={() => dispatch(addItemsToBasket())}>
                      {t('stocks.managerLayout.orderingHeaderInfoItems.addButton')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </HeaderStocksInfoContainer>
      )}
      <div className={filterByBrand ? 'is-brand-open' : null || filterBySubBrand ? 'is-sub-open' : null || filterByCategory ? 'is-cat-open' : null}>
        {filters.length > 0 && (
          <div className={columnOrder === ColumnsOrder.StockHoldingColumnsOrder ? 'toolbar short' : 'toolbar'}>
            <HeaderStocksFilter
              cssPosition={columnOrder === ColumnsOrder.ManagerLayoutColumnsOrder ? 'has-pdf-btn' : 'asd'}
              hasAnyProducts={hasAnyProducts}
              isCategoryOpen={isCategoryOpen}
              showLowProducts={columnOrder === ColumnsOrder.StockHoldingColumnsOrder}
              selectedBrandCount={allBrandSelected || filterBrandCount === 0 ? null : filterBrandCount}
              selectedSubBrandCount={allSubBrandSelected || filterSubBrandCount === 0 ? null : filterSubBrandCount}
              selectedCategoryCount={allCategorySelected || filterCategoryCount === 0 ? null : filterCategoryCount}
              clearBtn={clearBtn}
              clearFilter={onClearFilter}
              openDropdown={openDropdown}
              onSearch={onSearch}
              showFilterByBrand={showFilterByBrand}
              showFilterBySubBrand={showFilterBySubBrand}
              showFilterByCategory={showFilterByCategory}
              clearSearch={clearSearch}
              useProductModal={() => setModalUseProduct(true)}
              jsPdfGenerator={generatePdf}
              search={search}
              lowInStock={lowInStock}
              onCheckLowInStock={onCheckLowInStock}
              salonId={salonId}
              additionalClass={columnOrder === ColumnsOrder.StockHoldingColumnsOrder ? 'stock-order-filter' : ' '}
            >
              {filterByBrand && (
                <div className={filterByBrand ? 'filterByBrand' : 'hidden'}>
                  <div className="filter-sidebar animated fadeIn">
                    <FilterControl
                      field="brand"
                      isAllSelected={allBrandSelected}
                      filters={filters}
                      onCloseFilter={closeFilter}
                      toggleFilter={(f, name) => dispatch(toggleFilter(f, name))}
                      onSelectAllFilterValue={onSelectAllFilters}
                    />
                  </div>
                </div>
              )}
              {filterBySubBrand && (
                <div className={filterBySubBrand ? 'filterBySubBrand' : 'hidden'}>
                  <div className="filter-sidebar animated fadeIn">
                    <FilterControl
                      field="sub_brand"
                      isAllSelected={allSubBrandSelected}
                      filters={filters}
                      onCloseFilter={closeFilter}
                      toggleFilter={(f, name) => dispatch(toggleFilter(f, name))}
                      onSelectAllFilterValue={onSelectAllFilters}
                    />
                  </div>
                </div>
              )}
              {filterByCategory && (
                <div className={filterByCategory ? 'filterByCategory' : 'hidden'}>
                  <div className="filter-sidebar animated fadeIn">
                    <FilterControl
                      sorted={sorted}
                      field="category_name"
                      isAllSelected={allCategorySelected}
                      filters={filters}
                      onCloseFilter={closeFilter}
                      toggleFilter={(f, name) => dispatch(toggleFilter(f, name))}
                      onSelectAllFilterValue={onSelectAllFilters}
                    />
                  </div>
                </div>
              )}
            </HeaderStocksFilter>
          </div>
        )}
      </div>
      <div className={editedStock || filterByBrand || filterBySubBrand || filterByCategory ? 'stocks-layout sticky-container-stocks' : 'stocks-layout sticky-container-stocks'}>
        {columnOrder === ColumnsOrder.ManagerLayoutColumnsOrder && (
          <TableStocksManager
            isFetching={isFetching}
            salonId={salonId}
            allItemsSelected={allItemsSelected}
            sorted={sorted}
            editStock={(sid, d, flag) => dispatch(editStock(sid, d, flag))}
            editStockMinHolding={(sid, d) => dispatch(editStockMinHolding(sid, d))}
            editStockMaxHolding={(sid, d) => dispatch(editStockMaxHolding(sid, d))}
            onAddSort={onSortFunction}
            OnClickRow={openEditProductSidebar}
            onClickCheckbox={openManageItemsSidebar}
            search={search}
            columnOrder={columnOrder}
            toogleCheckAll={onCheckAllItems}
            toggleSelectItem={onSelectItem}
          />
        )}
        {columnOrder === ColumnsOrder.UsedSoldOverDateRange && <TableStocksUsedSold data={gridData} onAddSort={onSortFunction} OnClickRow={openEditProductSidebar} search={search} />}
        {columnOrder === ColumnsOrder.LastStockMovement && <TableLastStockMovement data={gridData} onAddSort={onSortFunction} OnClickRow={openEditProductSidebar} search={search} />}
        {columnOrder === ColumnsOrder.StockHoldingColumnsOrder && (
          <TableStocksOrdering
            isChecked={false}
            emptyBasket={orderItems.length > 0}
            allItemsSelected={allOrderItemsSelected}
            basketSaved={basketSaved}
            data={finalOrderingData}
            csvData={orderItems.map((e) => ({
              ...e,
              barcode: e.barcode ?? '-',
              quantity: e.quantity,
            }))}
            lowInStock={lowInStock}
            orderItems={groupOrderItems}
            orderItemsCount={orderItems.length}
            totalOrderItemsCost={totalOrderCost}
            search={search}
            salonId={salonId}
            geratedOrderNumber={geratedOrderNumber}
            onAddSort={onSortFunction}
            saveBasket={saveBasketOrder}
            deleteOrderItem={(id) => dispatch(deleteOrderItem(id))}
            generateOrderData={onGenerateOrderData}
            getLastOrderData={(sid, flag) => dispatch(getLastOrderData(sid, flag))}
            editOrderItem={(item) => dispatch(editOrderItem(item))}
            emptyOrderBasket={(sid) => dispatch(emptyOrderBasket(sid))}
            toggleOrderItem={(item, sel) => dispatch(toggleOrderItem(item, sel))}
            toogleCheckAll={onSelectAllOrderItems}
          />
        )}
      </div>
      <aside className={editedStock ? 'product-sidebar animated slideInRight' : 'product-sidebar hidden'}>
        {editedStock && (
          <EditManufacturerProductSidebar
            brandList={brandList}
            subBrandList={subBrandList}
            categoryList={categoryList}
            data={gridData}
            selectedData={editedStock}
            isSaving={isSavingStock}
            filters={filters}
            permission=""
            attemptDelete={attemptDelete}
            onClose={closeEditSidebar}
            onSave={onSaveEdit}
          />
        )}
      </aside>
      <aside className={manageItems ? 'product-sidebar animated slideInRight' : 'product-sidebar hidden'}>
        {manageItems && (
          <ManageItemsSidebarContainer
            brandList={brandList}
            subBrandList={subBrandList}
            categoryList={categoryList}
            data={gridData}
            onClose={closeManageSidebar}
            onDelete={onBulkDelete}
            onUpdateItems={onSaveBulkEdit}
            applyCostPriceChange={(args) => dispatch(applyCostPriceChange(args))}
            applyRetailPriceChange={(args) => dispatch(applyRetailPriceChange(args))}
            revertCostorRetailPrice={(args) => dispatch(revertCostorRetailPrice(args))}
          />
        )}
      </aside>
      {modalUseProduct && (
        <UseProductModal
          title={t('stocks.managerLayout.useProductModal.title')}
          onSave={handleSaveUseProduct}
          onCancel={() => {
            setModalUseProduct(false);
            setValueProduct('');
            setValueQuantity(1);
          }}
          disabled={!valueProduct || !valueQuantity}
        >
          <UseProductSearch
            items={renderProducts}
            dropdown={productDropdown}
            name={inputName}
            value={valueProduct}
            type="text"
            onChange={onSearchProduct}
            onClick={onClickSearchProduct}
            onBlur={onBlurProduct}
            placeholder={t('stocks.managerLayout.useProductModal.productNamePlaceholder')}
            label={t('stocks.managerLayout.useProductModal.productNameLabel')}
            autoFocus
          />
          <UseProductSearch
            items={renderProductsByQuantity}
            name={inputQuantity}
            value={valueQuantity}
            type="number"
            onChange={onSearchProduct}
            onClick={onClickSearchQuantity}
            label={t('stocks.managerLayout.useProductModal.quantityUsedLabel')}
            error={error && t('stocks.managerLayout.useProductModal.quantityUsedValidationErr')}
            inputMode="decimal"
          />
        </UseProductModal>
      )}

      {fromAnyToBool(modalSuccessful) && <ModalConfirmation title={t('stocks.managerLayout.confirmationModalTitle')} displayImage />}
      {fromAnyToBool(modalErrorMessage) && <ModalConfirmation title={modalErrorMessage} />}

      <div className={editedStock ? 'close-overlay' : 'hidden'} onClick={closeEditSidebar} role="button" tabIndex="0" />

      {fromAnyToBool(bulkDeleteModal) && (
        <ModalDeleteProduct
          icon="alert"
          title={t('stocks.managerLayout.deleteProductModal.title2')}
          message={t('stocks.managerLayout.deleteProductModal.description2')}
          onClose={cancelDeleteModal}
          onConfirm={onAllDelete}
        />
      )}
      {fromAnyToBool(deleteModalSuccess) && (
        <SalonModal tag="ManagerLayout__modal--success" isOpen variant="noFooter">
          <SalonModal.Header>
            <div className={CN.closeButtonContainer}>
              <Button variant="empty" onClick={close}>
                <Icon variant="close" variantInvert />
              </Button>
            </div>
          </SalonModal.Header>
          <SalonModal.Body>
            <IconStatus variant="success">
              <Icon variant="confirm" width="lg" />
            </IconStatus>
            <div className={CN.contentModal}>
              <div className={CN.titleModal}>{t('stocks.managerLayout.productDeleted')}</div>
            </div>
          </SalonModal.Body>
        </SalonModal>
      )}
    </div>
  );
};

export default ManagerLayout;
